import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import styles from './numbered-sections-text.module.scss';

export interface NumberedSectionsTextProps {
  /** The class name to apply to the component */
  className?: string;
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The headline of the section */
  headline: string;
  /** The function to call when the component enters the viewport */
  onEnter: () => void;
  /** The function to call when the component leaves the viewport */
  onLeave: () => void;
  /** The text to show */
  text: string;
}

const NumberedSectionsText = ({
  className,
  headline,
  ctaLinks = [],
  onEnter,
  onLeave,
  text,
}: NumberedSectionsTextProps) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const { isInView } = useIsInView(componentRef, {
    rootMargin: '0px 0px -50% 0px',
  });

  useEffect(() => {
    if (!componentRef?.current) return;

    if (isInView) {
      onEnter();
    } else {
      /*
        Since root margin is set to "-50% bottom" we need to calculate 50% of the viewport to detect if the component left from below.
        Instead this checks if the component went out of view from top and we skip the calculations.
      **/
      const isAboveViewport =
        componentRef.current.getBoundingClientRect().top < 0;

      if (!isAboveViewport) onLeave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, componentRef]);

  return (
    <div ref={componentRef} className={classNames(styles.component, className)}>
      <Typography className={styles.headline} tag="h4" tagStyle="bodyLargeBold">
        {headline}
      </Typography>

      <RichText className={styles.body} text={text} />

      {ctaLinks.length > 0 && (
        <div className={styles.links}>
          <CtaLinkList items={ctaLinks} />
        </div>
      )}
    </div>
  );
};

NumberedSectionsText.displayName = 'NumberedSectionsText';

export default NumberedSectionsText;
